import { SWRConfig } from 'swr';

import serverStore from '../../lib/utils/serverStore';

import {
  leplusMagazineService,
  leplusService,
  settingService,
} from '../../lib/services';
import { axiosInstance, getContentSmall, getIssueNumber, getIssueTitle } from '../../lib/utils';

import { withAlert, WithAlert } from '../../lib/hocs';
import Image from 'next/image';
import { AiFillCaretLeft, AiFillCaretRight, AiOutlinePlusCircle } from 'react-icons/ai';
import { useState, useEffect } from 'react';
import { useAppContext } from '../../lib/context';
import Link from 'next/link';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { setGraphContext } from '../../apollo-client';

import { Form, Field } from 'react-final-form';
import { Input } from '../../lib/components/form';
import { Button, Loader, Title } from '../../lib/components';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import getConfig from 'next/config';
import Head from 'next/head';
const { publicRuntimeConfig } = getConfig();

function Librairie({ fallback, articles }) {
  const { addAlert } = useAppContext();
  const [limit, setLimit] = useState(2);
  const [offset, setOffset] = useState(0);
  const [articlesAE, setArticles] = useState(articles);

  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const searchFunction = async (values) => {
    try {
      setIsSearching(true);
      if (!executeRecaptcha) {
        addAlert({
          status: "error",
          msg: publicRuntimeConfig.ERROR_500
        })
        setIsSearching(false);
        return;
      };
      const token = await executeRecaptcha();
      if (!token) {
        addAlert({
          status: "error",
          msg: publicRuntimeConfig.ERROR_500
        })
        setIsSearching(false);
        return;
      }

      setOffset(0);
      setSearch(values.search);
      const data = await leplusService.getLePlus(limit, 0, values.search, token);
      setArticles(data);
    } catch (err) {
      addAlert({
        status: err?.response?.data?.status,
        msg: err?.response?.data?.msg,
      });
    }
    setIsSearching(false);
  };

  const changePage = async (action) => {
    try {
      setIsSearching(true);
      if (!executeRecaptcha) {
        addAlert({
          status: "error",
          msg: publicRuntimeConfig.ERROR_500
        })
        setIsSearching(false);
        return;
      };
      const token = await executeRecaptcha();
      if (!token) {
        addAlert({
          status: "error",
          msg: publicRuntimeConfig.ERROR_500
        })
        setIsSearching(false);
        return;
      }

      let newOffset = offset;
      if (action === "next") {
        newOffset += limit
      } else {
        newOffset -= limit
      }
      setOffset(newOffset)
      const data = await leplusService.getLePlus(limit, newOffset, search, token);
      setArticles(data);
    } catch (err) {
      addAlert({
        status: err?.response?.data?.status,
        msg: err?.response?.data?.msg,
      });
    }
    setIsSearching(false);
  };

  return (
    <div className="flex-1 py-2">
      <SWRConfig value={{ fallback }}>
        <Head>
          <title>Animation & Éducation - Le Plus</title>
          <meta name="description" content="Découvrez notre rubrique Le Plus, du contenu pédagogique gratuit pour tous !"></meta>
        </Head>
        <div className="mx-5 md:mx-20 xl:mx-40">
          <Title>
            Le Plus
          </Title>
        </div>
        <section className="pt-8 bg-green-bg pb-14">
          <div className="mx-5 mb-10 md:mx-20 xl:mx-40">
            <Form
              onSubmit={searchFunction}
              render={({ handleSubmit }) => (
                <form className="flex flex-col max-w-lg xs:flex-row" onSubmit={handleSubmit}>
                  <Field
                    component={Input}
                    type="text"
                    name="search"
                    classInput="max-xs:rounded-md xs:rounded-l-md"
                    classContainer="w-full"
                  />
                  <Button
                    isLoading={isSearching}
                    disabled={isSearching}
                    link={false}
                    rounded={false}
                    type="submit"
                    text="Rechercher"
                    className="py-2 mt-3 text-xl uppercase rounded-md xs:pl-4 xs:pr-8 xs:py-0 xs:mt-0 xs:text-base xs:rounded-r-full"
                  />
                </form>
              )}
            />
          </div>
          <div className="relative grid grid-cols-1 gap-10 mx-5 md:mx-20 xl:mx-40 md:grid-cols-2">
            <div
              className={`
                ${offset > 0 ? "bg-white cursor-pointer hover:bg-green hover:text-white" : "bg-gray-200"} 
                absolute text-green p-3 text-5xl rounded-full top-1/2 right-[90%] xl:right-full -translate-x-1/2 -translate-y-1/2 z-50
                hidden md:block
              `}
              onClick={() => offset > 0 ? changePage("prev") : null}
            >
              <BsChevronLeft />
            </div>
            {isSearching &&
              <div className="col-span-2">
                <Loader />
              </div>
            }
            {(!isSearching && (!articlesAE || articlesAE?.data?.length === 0)) &&
              <p>Aucun article trouvé</p>
            }
            {!isSearching && articlesAE.data && articlesAE.data.map((article, key) => (
              <div key={key} className="relative flex flex-col p-5 bg-white xs:p-14 md:p-7 lg:p-14">
                <div>
                  <Image
                    src={article.picture.presignedUrl}
                    alt="Image présentant l'article Le Plus d'Animation et Éducation"
                    width={1200}
                    height={400}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                    }}
                  />
                </div>
                <h4 className="flex-1 mt-4 text-4xl font-semibold text-blue-darker">{article.title}</h4>
                <p className="flex-1 mb-5 text-2xl text-blue-darker">{article.description}</p>
                <p className="flex-1 text-lg text-blue-darker mb-14">{getContentSmall(article)}</p>
                <div className="self-center">
                  <Button
                    color="green"
                    action={`/le-plus/${article.urlRewrite}`}
                    className="block py-2 font-normal uppercase rounded-full px-14 text-blue-darker"
                    text="Lire la suite"
                    rounded={false}
                  />
                </div>
              </div>
            ))}
            <div
              className={`
                ${offset < articlesAE.length - limit ? "bg-white cursor-pointer hover:bg-green hover:text-white" : "bg-gray-200"}
                absolute text-green p-3 text-5xl rounded-full top-1/2 left-[90%] xl:left-full translate-x-1/2 -translate-y-1/2 z-50
                hidden md:block 
              `}
              onClick={() => offset < articlesAE.length - limit ? changePage("next") : null}
            >
              <BsChevronRight />
            </div>
          </div>
          <div className="flex items-center justify-center mt-10 md:hidden">
            <div
              className={`${offset > 0 ? "bg-white cursor-pointer hover:bg-green hover:text-white" : "bg-gray-200"} text-green p-3 text-6xl ml-2 rounded-full`}
              onClick={() => offset > 0 ? changePage("prev") : null}
            >
              <BsChevronLeft />
            </div>
            <div
              className={`${offset < articlesAE.length - limit ? "bg-white cursor-pointer hover:bg-green hover:text-white" : "bg-gray-200"} text-green p-3 text-6xl ml-2 rounded-full`}
              onClick={() => offset < articlesAE.length - limit ? changePage("next") : null}
            >
              <BsChevronRight />
            </div>
          </div>
        </section>
      </SWRConfig>
    </div>
  );
}

export const getServerSideProps = withAlert(async function getServerSideProps({ req, res }) {
  axiosInstance.setAxiosContext(req, res)
  setGraphContext(req, res)

  const articles = await leplusMagazineService.getLePlus();

  let setting = serverStore.get('setting');
  let fallback = {};
  fallback[settingService.SETTING_GET_SETTING_PATH] = setting;
  return {
    props: { fallback: fallback, setting: setting, articles },
  };
})

export default WithAlert(Librairie)